import {
  AlertTriangle,
  LoaderCircle,
  RotateCcw,
  X,
  type LucideIcon,
} from 'lucide-react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'

export function IconButton({ label, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return <button className="icon-button" aria-label={label} title={label} {...props} />
}

export function Button({
  tone = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'quiet' | 'danger' | 'outline' }) {
  return <button className={`button button--${tone} ${className}`} {...props} />
}

export function StatusBadge({ value }: { value: string }) {
  const labelMap: Record<string, string> = {
    active: '进行中', pending_activation: '待激活', under_review: '复核中', found: '已找到',
    cancelled: '已取消', disabled: '已停用', draft: '草稿', inactive: '已停用',
    issued: '待兑换', fulfillment_pending: '发放中', redeemed: '已兑换', expired: '已过期',
    voided: '已作废', success: '成功', failure: '失败', true: '启用', false: '停用',
  }
  const danger = ['under_review', 'disabled', 'voided', 'failure'].includes(value)
  const waiting = ['pending_activation', 'draft', 'issued', 'fulfillment_pending'].includes(value)
  return (
    <span className={`status-badge ${danger ? 'status-badge--danger' : waiting ? 'status-badge--waiting' : ''}`}>
      <span className="status-badge__dot" />
      {labelMap[value] || value || '未知'}
    </span>
  )
}

export function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <header className="page-header reveal">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action && <div className="page-header__action">{action}</div>}
    </header>
  )
}

export function Segmented<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T
  onChange(value: T): void
  items: Array<{ value: T; label: string; count?: number }>
}) {
  return (
    <div className="segmented" role="tablist">
      {items.map(item => (
        <button
          type="button"
          role="tab"
          aria-selected={value === item.value}
          className={value === item.value ? 'is-active' : ''}
          key={item.value}
          onClick={() => onChange(item.value)}
        >
          {item.label}
          {typeof item.count === 'number' && <span>{item.count}</span>}
        </button>
      ))}
    </div>
  )
}

export function QueryState({
  loading,
  error,
  empty,
  onRetry,
  emptyText = '暂无符合条件的记录',
  children,
}: {
  loading: boolean
  error: Error | null
  empty: boolean
  onRetry(): void
  emptyText?: string
  children: ReactNode
}) {
  if (loading) {
    return <div className="query-state"><LoaderCircle className="spin" /><span>正在整理现场数据…</span></div>
  }
  if (error) {
    return (
      <div className="query-state query-state--error">
        <AlertTriangle />
        <div><strong>读取失败</strong><p>{error.message}</p></div>
        <Button tone="outline" onClick={onRetry}><RotateCcw size={16} />重试</Button>
      </div>
    )
  }
  if (empty) return <div className="query-state"><span className="empty-mark">∅</span><span>{emptyText}</span></div>
  return <>{children}</>
}

export function Dialog({
  open,
  title,
  description,
  onClose,
  children,
}: {
  open: boolean
  title: string
  description?: string
  onClose(): void
  children: ReactNode
}) {
  if (!open) return null
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={event => {
      if (event.currentTarget === event.target) onClose()
    }}>
      <section className="dialog" role="dialog" aria-modal="true" aria-labelledby="dialog-title">
        <header>
          <div><p className="eyebrow">受控操作</p><h2 id="dialog-title">{title}</h2>{description && <p>{description}</p>}</div>
          <IconButton label="关闭" onClick={onClose}><X size={20} /></IconButton>
        </header>
        {children}
      </section>
    </div>
  )
}

export function MetricCard({
  icon: Icon,
  label,
  value,
  note,
  tone = 'normal',
}: {
  icon: LucideIcon
  label: string
  value: number
  note: string
  tone?: 'normal' | 'attention'
}) {
  return (
    <article className={`metric-card metric-card--${tone} reveal`}>
      <div className="metric-card__icon"><Icon size={19} /></div>
      <p>{label}</p>
      <strong>{String(value).padStart(2, '0')}</strong>
      <small>{note}</small>
    </article>
  )
}

export function formatTime(value: number): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(value)
}

export function roleLabel(role: string): string {
  const labels: Record<string, string> = {
    admin_super: '超级管理员', admin_operator: '运营', admin_customer_service: '客服', admin_auditor: '审计',
    qr_treasure_publisher: '官方藏宝', qr_voucher_publisher: '券宝发布', customer_service: '客服发放',
    voucher_redeemer: '现场核销', redemption_admin: '核销管理',
  }
  return labels[role] || role
}
